/**
 * "There is a new one" - wherever you happen to be standing.
 *
 * One check per launch, well after the app is usable, and a drop-down that says
 * what the update is before it is taken: version, build, Native or OTA.
 *
 * Three rules it lives by.
 *
 * It never costs anything at launch. The check is scheduled seconds after the
 * first paint and then only on an idle callback, so it cannot land in front of
 * auth, the router, or the first screen's own queries. A slow or failed check
 * does nothing at all and nobody finds out.
 *
 * It owns no space in the layout. Fixed over the top of the screen, not a row
 * pushed into the flow. A banner that reflows the page while a thumb is moving
 * yanks the content out from under it, which is a bug this app has already paid
 * for once on the Cards screen.
 *
 * Skip means skip this build, on the device, for good. It is remembered per
 * kind, so passing on a bundle does not silence a store release, and it only
 * comes back when something newer than what was skipped exists.
 */

import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { ArrowDownToLine } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import {
  updatesToOffer, applyOta, formatRelease, skipRelease, nativeRelationLine,
} from '../services/appUpdate';

/**
 * Screens the banner stays off.
 *
 * The scanner is a live camera the person is aiming at a card, and Trade Now is
 * a takeover that runs until the trade is done or cancelled - a panel dropping
 * into either one is genuinely bad, not just untidy. The rest are screens where
 * an update prompt makes no sense: the Game Boy intro, signing in and setting up
 * an account, the public shared binder, and Support itself, which has the whole
 * update card on it already.
 *
 * Matched as prefixes, so /trade-now/:id and /trade-now/:id/pick/:who are
 * covered by one entry.
 */
const QUIET_PATHS = [
  '/bulk-scanner',
  '/trade-now',
  '/tn/',
  '/game',
  '/login',
  '/signup',
  '/parent-signup',
  '/parent-quick-setup',
  '/setup-login',
  '/profile-picker',
  '/post',
  '/support',
  '/update',
  '/b/',
];

// Long enough that the first screen has rendered and settled. The idle callback
// after it is what actually keeps this out of the way; this is the floor.
const LAUNCH_DELAY_MS = 6000;

// One check per launch. A module flag, not state: this component never
// unmounts, and an over-the-air install restarts the whole JS context anyway.
let checkedThisLaunch = false;

export default function UpdateBanner() {
  const { theme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [found, setFound] = useState(null);
  const [busy, setBusy] = useState(false);
  const aliveRef = useRef(true);

  useEffect(() => {
    aliveRef.current = true;
    // Nothing to offer on the web: there is no store build and no shell to put
    // a bundle in. Skipping the call keeps the launch completely untouched.
    if (checkedThisLaunch || !Capacitor.isNativePlatform()) return undefined;
    checkedThisLaunch = true;

    let idleHandle = null;
    const run = async () => {
      try {
        const offer = await updatesToOffer();
        if (!aliveRef.current || offer.error) return;
        if (offer.native || offer.ota) setFound({ native: offer.native, ota: offer.ota });
      } catch {
        /* a launch check that fails is a launch check nobody notices */
      }
    };

    const timer = setTimeout(() => {
      if (window.requestIdleCallback) idleHandle = window.requestIdleCallback(run, { timeout: 4000 });
      else run();
    }, LAUNCH_DELAY_MS);

    return () => {
      aliveRef.current = false;
      clearTimeout(timer);
      if (idleHandle != null && window.cancelIdleCallback) window.cancelIdleCallback(idleHandle);
    };
  }, []);

  const quiet = QUIET_PATHS.some((p) => location.pathname === p || location.pathname.startsWith(p));
  // Held, not dropped. Someone who launches into the scanner sees this when
  // they come back out rather than losing it for the whole run.
  if (!found || quiet) return null;

  const both = Boolean(found.native && found.ota);
  const kind = found.native ? 'native' : 'ota';
  const row = found.native || found.ota;

  const skip = () => {
    if (found.native) skipRelease('native', found.native.build);
    if (found.ota) skipRelease('ota', found.ota.build);
    setFound(null);
  };

  const take = async () => {
    // Both kinds waiting is a choice, not a default. Support shows them side by
    // side with what each one is.
    if (both) { setFound(null); navigate('/support'); return; }
    // Native is a mechanism per platform, and it is not this component's to
    // pick. Support asks appUpdate for the actions and renders them.
    if (found.native) { setFound(null); navigate('/support?check=1'); return; }
    setBusy(true);
    try {
      await applyOta(found.ota);
      // On success the plugin swaps the bundle in and reloads; nothing after
      // this line runs.
    } catch (err) {
      console.warn('[update] banner install failed', err);
      if (!aliveRef.current) return;
      setBusy(false);
      setFound(null);
      navigate('/support?check=1');
    }
  };

  const styles = makeStyles(theme);
  // The build number rides on every line. An OTA does not change the shell, so
  // the build it shows is the one already installed.
  const onBuild = found.installed?.build || 0;
  const label = both
    ? `${formatRelease('native', found.native, onBuild)}  ·  ${formatRelease('ota', found.ota, onBuild)}`
    : formatRelease(kind, row, onBuild);
  // Only for an OTA, and only when we know both dates: a bundle published
  // before the installed shell is older code than is already there.
  const relation = kind === 'ota' && !both
    ? nativeRelationLine(row, found.installedNative)
    : '';
  const action = both ? 'See both' : (found.native ? 'Get it' : 'Download');

  return (
    <>
      <style>{`
        @keyframes cc-update-drop {
          from { transform: translateY(-140%); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        .cc-update-banner { animation: cc-update-drop 280ms cubic-bezier(0.22, 1, 0.36, 1) both; }
        @media (prefers-reduced-motion: reduce) {
          .cc-update-banner { animation: none; }
        }
      `}</style>
      <div className="cc-update-banner" style={styles.wrap} role="status" aria-live="polite">
        <div style={styles.top}>
          <div style={styles.badge}>
            <ArrowDownToLine size={17} color={theme.accent} />
          </div>
          <div style={styles.textCol}>
            <div style={styles.title}>{both ? 'Two updates ready' : 'Download new update'}</div>
            <div style={styles.label}>{label}</div>
            {relation && <div style={styles.relation}>{relation}</div>}
          </div>
        </div>
        <div style={styles.actions}>
          <button type="button" style={styles.skipBtn} onClick={skip} disabled={busy}>Skip</button>
          <button type="button" style={{ ...styles.takeBtn, opacity: busy ? 0.6 : 1 }} onClick={take} disabled={busy}>
            {busy ? 'Installing' : action}
          </button>
        </div>
      </div>
    </>
  );
}

const makeStyles = (theme) => ({
  wrap: {
    position: 'fixed',
    // The status bar is measured, never guessed. Same variables the shell uses.
    top: 'calc(var(--safe-area-top, 0px) + 8px)',
    left: 'calc(var(--safe-area-left, 0px) + 10px)',
    right: 'calc(var(--safe-area-right, 0px) + 10px)',
    // Above the screens and the floating nav, below the full-screen modals and
    // sheets, which start at 9998.
    zIndex: 9000,
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    padding: 14,
    borderRadius: 14,
    backgroundColor: theme.bgCard,
    border: `1px solid ${theme.accent}`,
    boxShadow: '0 12px 30px rgba(0, 0, 0, 0.45)',
  },
  top: { display: 'flex', alignItems: 'center', gap: 12 },
  badge: {
    width: 34, height: 34, borderRadius: 17, flexShrink: 0,
    backgroundColor: theme.accentBg,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  textCol: { minWidth: 0 },
  title: { fontSize: 14, fontWeight: 700, color: theme.text, lineHeight: 1.3 },
  label: {
    fontSize: 13, fontWeight: 600, color: theme.accent, marginTop: 2,
    fontVariantNumeric: 'tabular-nums', lineHeight: 1.35,
  },
  relation: { fontSize: 11, opacity: 0.72, marginTop: 2, lineHeight: 1.35 },
  actions: { display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8 },
  skipBtn: {
    padding: '9px 14px', borderRadius: 10,
    border: `1px solid ${theme.border}`,
    backgroundColor: 'transparent', color: theme.textMuted,
    fontSize: 13, fontWeight: 600, cursor: 'pointer',
  },
  takeBtn: {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: '9px 16px', borderRadius: 10,
    border: '1px solid transparent',
    backgroundColor: theme.accent, color: '#ffffff',
    fontSize: 13, fontWeight: 700, cursor: 'pointer',
  },
});
